'use client';

import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  SheetTrigger,
  SheetContent,
  Sheet,
  SheetClose,
} from '@eliadmin/core/src/components/ui/sheet';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { arePathsEqual } from '@eliadmin/core/src/utils/url-utils';
import { useQuery } from '@tanstack/react-query';
import Link from '@eliadmin/core/src/components/link';
import { usePathname } from 'next/navigation';
import React from 'react';
import { Fragment } from 'react';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { HeaderLogo } from '../header/header-logo';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { PhoneCallIcon } from 'lucide-react';
import { PiPhoneFill } from 'react-icons/pi';
import { RiPhoneFill } from 'react-icons/ri';
import { FaPhone } from 'react-icons/fa6';
import type { GeneralLayout } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { SearchButton } from '@eliadmin/core/src/components/header/search-button';
import { MobileSearchProvider } from '@eliadmin/core/src/components/header/mobile-search';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { CartBadge } from '@eliadmin/core/src/components/header/cart-badge';
import { FavoritesBadge } from '../header/header-favorites-button';
import { Badge } from '@eliadmin/core/src/components/ui/badge';

export type BottomBarProps = {
  className?: {
    root?: string;
  };
  items?: BottomBarItemProps[];
};

export function BottomBar({ items = HEADER_CONFIG.bottomBar.items, ...props }: BottomBarProps) {
  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 top-auto z-50 flex h-14 flex-row  items-center justify-between space-x-4 bg-background px-4 py-1 shadow-[0px_0px_15px_rgba(0,0,0,0.1)] sm:hidden',
        'ea-bottom-bar',
        props.className?.root,
      )}
    >
      {items?.map(({ key, ...item }) => (
        <BottomBarItem key={key} itemKey={key} {...item} />
      ))}
    </div>
  );
}

export type BottomBarItemProps = {
  key: string;
  itemKey?: string;
  icon?: React.ReactNode;
  label?: string;
  className?: {
    root?: string;
    iconContainer?: string;
    label?: string;
    active?: string;
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  href?: string | (() => string);
  isMenu?: boolean;
};
export function BottomBarItem(props: BottomBarItemProps) {
  if (!props.icon && !props.label) {
    throw new Error('Either icon or label must be provided');
  }
  const pathname = usePathname();
  let href = typeof props.href === 'function' ? props.href() : props.href ? props.href : '#';
  const isActive = arePathsEqual(pathname, href);
  const me = useCustomerStore(s => s.me);
  const isCustomerStoreInitialized = useCustomerStore(s => s.isInitialized);
  const locale = useLocaleStore(s => s.currentLocale);
  const generalLayout = useGlobalStore(s => s.generalLayout);

  const favoriteItems = useQuery({
    queryKey: [CacheKey.FAVORITE_PRODUCTS, locale, me?.authToken ?? null, props.itemKey],
    queryFn: () => {
      if (props.itemKey != 'favorites') return;
      return fetchProducts({
        locale,
        authToken: me ? me!.authToken : '',
        query: { favorite: true, results: 9999 },
      });
    },
    enabled: isCustomerStoreInitialized && !!me?.authToken && props.itemKey === 'favorites',
  });

  if (
    !me &&
    isCustomerStoreInitialized &&
    (href === ROUTES.PROFILE() || href === ROUTES.FAVORITES())
  ) {
    href = ROUTES.SIGN_IN();
  }

  const { t } = useTranslations();

  if (props.isMenu) {
    return (
      <MobileSearchProvider>
        <SearchButton variant="ghost" />
      </MobileSearchProvider>
    );
  }

  const favoriteItemsCount = favoriteItems?.data?.data?.length ?? 0;

  const countText =
    favoriteItemsCount > 99 ? (
      <span>
        99<sup>+</sup>
      </span>
    ) : (
      favoriteItemsCount
    );

  return (
    <Link
      href={href ?? '#'}
      className={cn(
        'flex flex-col items-center space-y-1',
        'ea-bottom-bar__item text-muted-foreground',
        props.className?.root,
        {
          'ea-bottom-bar__item--active text-accent': isActive,
        },
      )}
      prefetch={false}
    >
      {props.itemKey === 'cart' && (
        <div className="relative">
          <CartBadge
            className={cn(
              '-right-3 top-2 h-5 w-5 bg-destructive-foreground font-light hover:bg-destructive-foreground',
            )}
            variant={HEADER_CONFIG.headerMain.cart.badgeVariant}
          />
        </div>
      )}
      {props.itemKey === 'favorites' && (
        <div className="relative">
          <Badge
            className={cn(
              'pointer-events-none absolute -right-3 top-2 aspect-square h-5 w-5 -translate-y-1/2 translate-x-1/2 justify-center bg-destructive-foreground font-light text-background',
              'ea-favorites-button__badge',
            )}
            size="circle"
          >
            {countText}
          </Badge>
        </div>
      )}
      {props.icon && (
        <div
          className={cn(
            'flex h-6 w-6 flex-col items-center justify-center',
            'ea-bottom-bar__item__icon-container',
            props.className?.iconContainer,
          )}
        >
          {props.icon}
        </div>
      )}
      {props.label && (
        <div
          className={cn(
            'text-center text-xs',
            'ea-bottom-bar__item__label',
            props.className?.label,
          )}
        >
          {t(props.label)}
        </div>
      )}
    </Link>
  );
}
